import { Colors, Text } from '@amelio/ui-library';

import { SCORE_MAX } from '../calibrationConstants';
import { CHART_LEGEND_ENTRY, CHART_LEGEND_LAYOUT, CHART_LEGEND_SWATCH } from '../calibrationStyles';
import { formatScore } from '../utils/formatScore';

import type { CalibrationReport } from '@/types/calibration';

import { useAmelioTranslations } from '@/hooks';

export interface ChartLegendFooterProps {
    report: CalibrationReport;
    language: string;
}

interface LegendSwatchProps {
    /** Tailwind background utility of the bar this entry stands for. */
    colorClassName: string;
    label: string;
}

const LegendSwatch = ({ colorClassName, label }: LegendSwatchProps) => (
    <span className={CHART_LEGEND_ENTRY}>
        <span
            aria-hidden
            className={`${CHART_LEGEND_SWATCH} ${colorClassName}`}
        />

        <Text
            size={'small'}
            color={Colors.BASE_500}
        >
            {label}
        </Text>
    </span>
);

/**
 * Legend closing the rating-level chart: what each bar colour means, then the two cohort figures
 * the chart is read against. They sit here rather than in a banner of their own above the chart —
 * a reader needs them while looking at the bars, not before.
 */
export const ChartLegendFooter = ({ report, language }: ChartLegendFooterProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div
            className={CHART_LEGEND_LAYOUT}
            data-testid={'calibration-chart-legend'}
        >
            <LegendSwatch
                colorClassName={'bg-primary'}
                label={t('calibration.deviation.legendAbove')}
            />

            <LegendSwatch
                colorClassName={'bg-secondary'}
                label={t('calibration.deviation.legendBelow')}
            />

            <Text
                size={'small'}
                color={Colors.BASE_400}
            >
                {t('calibration.deviation.cohortAverage', {
                    score: formatScore({ value: report.cohortAverage, language }),
                    max: SCORE_MAX,
                })}
            </Text>

            <Text
                size={'small'}
                color={Colors.BASE_400}
            >
                {`${t('calibration.deviation.evaluatedTotal', {
                    count: report.evaluatedCount,
                })} · ${t('calibration.deviation.managerTotal', {
                    count: report.managers.length,
                })}`}
            </Text>
        </div>
    );
};
